'use client';

/**
 * Unified auth hooks backed by Better Auth.
 *
 * Components should import from here instead of directly from Better Auth
 * to maintain a stable interface and enable future adapter changes.
 */

import { authClient } from '@/lib/auth-client';

/** Safely extract the role from a Better Auth user object. */
function getUserRole(user: Record<string, unknown>): 'user' | 'admin' | undefined {
  const role = user.role;
  return role === 'admin' || role === 'user' ? role : undefined;
}

/**
 * Returns the current authentication state.
 */
export function useAuth() {
  const session = authClient.useSession();

  return {
    isLoaded: !session.isPending,
    isSignedIn: !!session.data?.user,
    userId: session.data?.user?.id ?? null,
    sessionId: session.data?.session?.id ?? null,
    signOut: () => authClient.signOut(),
  };
}

/**
 * Returns the current user object.
 */
export function useUser() {
  const session = authClient.useSession();

  const user = session.data?.user
    ? {
        id: session.data.user.id,
        firstName: session.data.user.name,
        lastName: null as string | null,
        email: session.data.user.email,
        imageUrl: session.data.user.image,
        publicMetadata: {
          database_id: session.data.user.id,
          role: getUserRole(session.data.user as Record<string, unknown>),
        },
      }
    : null;

  return {
    isLoaded: !session.isPending,
    isSignedIn: !!session.data?.user,
    user,
  };
}
